import {
  bindThemeParamsCssVars,
  hapticFeedbackImpactOccurred,
  hapticFeedbackSelectionChanged,
  hideBackButton,
  mountMainButton,
  offMainButtonClick,
  onMainButtonClick,
  setMainButtonParams,
  shareURL,
  openTelegramLink,
  showPopup,
  init,
  isTMA,
  miniAppReady,
  mountBackButton,
  mountMiniAppSync,
  mountThemeParamsSync,
  onBackButtonClick,
  openQrScanner,
  retrieveLaunchParams,
  retrieveRawInitData,
  showBackButton,
} from "@telegram-apps/sdk";

let insideTelegram = false;

function safe(fn: () => void) {
  try {
    fn();
  } catch {
    // Метод не поддерживается этой версией Telegram — работаем без него.
  }
}

export function initTelegram(): void {
  insideTelegram = isTMA();
  if (!insideTelegram) return;
  document.documentElement.classList.add("tg");
  init();
  safe(() => mountThemeParamsSync());
  safe(() => bindThemeParamsCssVars());
  safe(() => mountMiniAppSync());
  safe(() => mountBackButton());
  safe(() => mountMainButton());
  safe(() => miniAppReady());
}

export function isInsideTelegram(): boolean {
  return insideTelegram;
}

/**
 * Заголовок авторизации для API. Вне Telegram (разработка в браузере) —
 * «dev <telegram_id>» из ?dev=… или localStorage; сервер принимает его только при DEV_AUTH=true.
 */
export function authHeader(): string | null {
  if (insideTelegram) {
    const raw = retrieveRawInitData();
    return raw ? `tma ${raw}` : null;
  }
  const fromQuery = new URLSearchParams(location.search).get("dev");
  if (fromQuery) localStorage.setItem("devTelegramId", fromQuery);
  const devId = fromQuery ?? localStorage.getItem("devTelegramId");
  return devId ? `dev ${devId}` : null;
}

/** Ключ пользователя для локального кэша: на одном устройстве могут работать разные аккаунты. */
export function userCacheKey(): string {
  if (insideTelegram) {
    try {
      return `tg${retrieveLaunchParams(true).tgWebAppData?.user?.id ?? "anon"}`;
    } catch {
      return "anon";
    }
  }
  const dev = new URLSearchParams(location.search).get("dev") ?? localStorage.getItem("devTelegramId");
  return `dev${dev ?? "anon"}`;
}

export function telegramLanguage(): "ru" | "uz" | null {
  if (!insideTelegram) return null;
  try {
    const lp = retrieveLaunchParams(true);
    const code = lp.tgWebAppData?.user?.languageCode;
    return code?.startsWith("uz") ? "uz" : code ? "ru" : null;
  } catch {
    return null;
  }
}

// Кнопка «Назад» Telegram — стек: верхний обработчик (открытый лист или вложенный экран) получает нажатие.
const backStack: (() => void)[] = [];
let backBound = false;

/** Показать системную «Назад», пока жив обработчик. Возвращает функцию снятия. */
export function pushBackHandler(handler: () => void): () => void {
  if (!insideTelegram) return () => undefined;
  if (!backBound) {
    safe(() => onBackButtonClick(() => backStack.at(-1)?.()));
    backBound = true;
  }
  backStack.push(handler);
  safe(() => showBackButton());
  return () => {
    const i = backStack.lastIndexOf(handler);
    if (i !== -1) backStack.splice(i, 1);
    if (backStack.length === 0) safe(() => hideBackButton());
  };
}

export function hapticSelect(): void {
  if (insideTelegram) safe(() => hapticFeedbackSelectionChanged());
}

export function hapticTap(): void {
  if (insideTelegram) safe(() => hapticFeedbackImpactOccurred("light"));
}

export function canScanQr(): boolean {
  if (!insideTelegram) return false;
  try {
    return openQrScanner.isAvailable();
  } catch {
    return false;
  }
}

/** Нативный сканер Telegram; ждёт QR фискального чека. null — пользователь закрыл сканер. */
export async function scanReceiptQr(prompt: string): Promise<string | null> {
  const result = await openQrScanner({ text: prompt, capture: (qr) => qr.includes("soliq.uz") });
  return result ?? null;
}

/** Нижняя кнопка Telegram (MainButton): параметры и обработчик нажатия. Вне Telegram ничего не делают. */
export function setMainButton(params: { text?: string; visible?: boolean; enabled?: boolean; loading?: boolean }): void {
  if (!insideTelegram) return;
  safe(() =>
    setMainButtonParams({
      ...(params.text !== undefined && { text: params.text }),
      ...(params.visible !== undefined && { isVisible: params.visible }),
      ...(params.enabled !== undefined && { isEnabled: params.enabled }),
      ...(params.loading !== undefined && { isLoaderVisible: params.loading }),
    }),
  );
}

export function onMainButton(handler: () => void): () => void {
  if (!insideTelegram) return () => undefined;
  safe(() => onMainButtonClick(handler));
  return () => safe(() => offMainButtonClick(handler));
}

/** Подтверждение опасного действия: нативное окно Telegram, в браузере — confirm(). */
export async function confirmDialog(message: string, okText: string): Promise<boolean> {
  if (insideTelegram) {
    try {
      const id = await showPopup({ message, buttons: [{ id: "ok", type: "destructive", text: okText }, { type: "cancel" }] });
      return id === "ok";
    } catch {
      // старый клиент — обычный confirm
    }
  }
  return window.confirm(message);
}

/** Открыть чат с ботом (из Telegram — без выхода из приложения). */
export function openBotChat(username: string): void {
  const url = `https://t.me/${username}`;
  if (insideTelegram) {
    try {
      openTelegramLink(url);
      return;
    } catch {
      // ниже — обычная ссылка
    }
  }
  window.open(url, "_blank");
}

/** Поделиться ссылкой: в Telegram — выбор чата, в браузере — системное меню или буфер обмена. */
export async function shareLink(url: string, text: string): Promise<"shared" | "copied"> {
  if (insideTelegram) {
    try {
      shareURL(url, text);
      return "shared";
    } catch {
      // старый клиент — копируем
    }
  }
  if (typeof navigator.share === "function") {
    try {
      await navigator.share({ url, text });
      return "shared";
    } catch {
      // отмена — копируем
    }
  }
  await navigator.clipboard?.writeText(`${text} ${url}`).catch(() => undefined);
  return "copied";
}

/** Раздел, с которого открыть приложение: ?screen=home|report|ops|lists|more|debts|admin|add (кнопки из бота). */
export function initialScreen(): string | null {
  return new URLSearchParams(location.search).get("screen");
}
